import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../store/authStore';
import { isFreeAdmin } from '../lib/freeAdmins';
import { useAllLevels, useGenerateLevel, useManageLevel, useDeleteLevel } from '../hooks/useGameLevels';
import { PageWrapper } from '../components/layout/PageWrapper';
import { Seo } from '../components/Seo';
import { GameTimeline } from '../components/game/GameTimeline';
import { OrnamentDivider } from '../components/ui/OrnamentDivider';
import type { StoredLevel } from '../types/gameApi';
import type { Difficulty, PlayerAssignment } from '../types/game';

const DIFFICULTIES: { value: Difficulty; label: string }[] = [
  { value: 'beginner', label: 'Новичок' },
  { value: 'intermediate', label: 'Средний' },
  { value: 'advanced', label: 'Сложный' },
];

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  generating: { label: 'Генерируется…', cls: 'text-mafia-gold border-mafia-gold/40 bg-mafia-gold/5' },
  error: { label: 'Ошибка', cls: 'text-mafia-crimson border-mafia-crimson/40 bg-mafia-crimson/10' },
  draft: { label: 'Черновик', cls: 'text-mafia-muted border-mafia-border bg-mafia-card' },
  scheduled: { label: 'Запланировано', cls: 'text-amber-400 border-amber-700/40 bg-amber-950/20' },
  published: { label: 'Опубликовано', cls: 'text-emerald-400 border-emerald-700/40 bg-emerald-950/20' },
  archived: { label: 'Архив', cls: 'text-mafia-muted/50 border-mafia-border/30 bg-transparent' },
};

const DIFFICULTY_CLS: Record<string, string> = {
  beginner: 'text-emerald-400 border-emerald-700/40 bg-emerald-950/20',
  intermediate: 'text-mafia-gold border-mafia-gold/40 bg-mafia-gold/5',
  advanced: 'text-mafia-crimson border-mafia-crimson/40 bg-mafia-crimson/10',
};

function buildAssignments(stored: StoredLevel): PlayerAssignment[] {
  const level = stored.levelData;
  if (!level) return [];
  return Array.from({ length: 10 }, (_, i) => {
    const seat = i + 1;
    const role = (level.solution?.[seat] as string | undefined) ?? 'grey';
    const isKilled = level.nights?.some((n) => n.killedPlayer === seat);
    const isEvicted = level.days?.some((d) => d.evictedPlayer === seat);
    return { seat, role: role as PlayerAssignment['role'], status: isKilled ? 'killed' : isEvicted ? 'evicted' : 'alive' };
  });
}

function LevelRow({ stored, onManage }: { stored: StoredLevel; onManage: (s: StoredLevel) => void }) {
  const deleteLevel = useDeleteLevel();
  const [expanded, setExpanded] = useState(false);

  async function quickDelete() {
    if (!confirm('Удалить запись?')) return;
    await deleteLevel.mutateAsync(stored.levelId);
  }
  const badge = STATUS_BADGE[stored.status];
  const diffCls = DIFFICULTY_CLS[stored.difficulty] ?? '';
  const level = stored.levelData;

  return (
    <div className={`rounded border transition-colors ${stored.status === 'archived' ? 'border-mafia-border/30 opacity-50' : 'border-mafia-border'}`}>
      <div className="p-4 flex items-center gap-3 flex-wrap">
        {/* Status + difficulty */}
        <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${badge.cls}`}>
          {badge.label}
        </span>
        <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${diffCls}`}>
          {stored.difficulty}
        </span>

        {/* Title */}
        <span className="font-display text-mafia-paper text-base flex-1 min-w-0 truncate">
          {stored.title || stored.levelId}
        </span>

        {/* Meta */}
        <span className="text-[10px] font-mono text-mafia-muted whitespace-nowrap">
          {new Date(stored.createdAt).toLocaleDateString('ru')}
          {stored.publishAt && stored.status === 'scheduled' && (
            <> → {new Date(stored.publishAt).toLocaleString('ru', { dateStyle: 'short', timeStyle: 'short' })}</>
          )}
        </span>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExpanded((o) => !o)}
            className="text-[10px] font-mono text-mafia-muted hover:text-mafia-paper-dim transition-colors"
          >
            {expanded ? 'Скрыть' : 'Просмотр'}
          </button>
          {(stored.status === 'generating' || stored.status === 'error') && (
            <button
              onClick={quickDelete}
              disabled={deleteLevel.isPending}
              className="text-[10px] font-mono text-mafia-crimson/70 hover:text-mafia-crimson transition-colors border border-mafia-crimson/30 px-2 py-1 rounded disabled:opacity-40"
            >
              Удалить
            </button>
          )}
          {stored.status !== 'archived' && stored.status !== 'generating' && stored.status !== 'error' && (
            <button
              onClick={() => onManage(stored)}
              className="btn-gold text-[10px] px-2 py-1"
            >
              Управление
            </button>
          )}
        </div>
      </div>

      {/* Expanded preview */}
      <AnimatePresence>
        {expanded && level && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-mafia-border/50"
          >
            <div className="p-4 flex flex-col gap-4">
              <p className="text-xs text-mafia-paper-dim whitespace-pre-line leading-relaxed">{level.brief}</p>
              <GameTimeline
                nights={level.nights ?? []}
                days={level.days ?? []}
                sheriffClaims={level.sheriffClaims ?? []}
                assignments={buildAssignments(stored)}
                focusedSeat={null}
                visibleDay={0}
              />
              <details>
                <summary className="text-[10px] font-mono text-mafia-muted cursor-pointer hover:text-mafia-paper-dim">
                  Ход решения
                </summary>
                <p className="text-xs text-mafia-paper-dim mt-2 leading-relaxed whitespace-pre-line">{stored.walkthrough}</p>
              </details>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ManageModal({
  stored,
  onClose,
}: {
  stored: StoredLevel;
  onClose: () => void;
}) {
  const manage = useManageLevel();
  const deleteLevel = useDeleteLevel();
  const [scheduleDate, setScheduleDate] = useState('');
  const [showSchedule, setShowSchedule] = useState(false);

  async function act(action: 'publish' | 'schedule' | 'unpublish') {
    await manage.mutateAsync({ levelId: stored.levelId, action, publishAt: scheduleDate || undefined });
    onClose();
  }
  const isDraft = stored.status === 'draft';

  async function doDelete() {
    const msg = isDraft
      ? 'Удалить черновик? Это действие нельзя отменить.'
      : 'Архивировать уровень? Он исчезнет из игры для игроков.';
    if (!confirm(msg)) return;
    await deleteLevel.mutateAsync(stored.levelId);
    onClose();
  }

  const busy = manage.isPending || deleteLevel.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="card-vintage w-full max-w-sm p-6 flex flex-col gap-4"
      >
        <h3 className="font-display text-xl text-mafia-gold">{stored.title}</h3>
        <p className="text-xs text-mafia-muted font-mono">Статус: {STATUS_BADGE[stored.status]?.label}</p>

        <OrnamentDivider variant="dim" />

        <div className="flex flex-col gap-2">
          {(stored.status === 'draft' || stored.status === 'scheduled') && (
            <button onClick={() => act('publish')} disabled={busy} className="btn-gold-solid w-full py-2 text-sm">
              Опубликовать сейчас
            </button>
          )}

          {stored.status === 'draft' && (
            <>
              {!showSchedule ? (
                <button onClick={() => setShowSchedule(true)} disabled={busy} className="btn-gold w-full py-2 text-sm">
                  Запланировать публикацию
                </button>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="datetime-local"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="flex-1 bg-mafia-card border border-mafia-border text-mafia-paper text-xs font-mono px-2 py-1.5 rounded focus:border-mafia-gold/50 outline-none"
                  />
                  <button
                    onClick={() => act('schedule')}
                    disabled={!scheduleDate || busy}
                    className="btn-gold px-3 text-xs disabled:opacity-40"
                  >
                    ОК
                  </button>
                </div>
              )}
            </>
          )}

          {stored.status === 'published' && (
            <button onClick={() => act('unpublish')} disabled={busy} className="btn-gold w-full py-2 text-sm">
              Снять с публикации
            </button>
          )}

          {stored.status === 'scheduled' && (
            <button onClick={() => act('unpublish')} disabled={busy} className="btn-gold w-full py-2 text-sm">
              Отменить расписание
            </button>
          )}

          <button onClick={doDelete} disabled={busy} className="w-full py-2 text-xs font-mono text-mafia-crimson/70 hover:text-mafia-crimson transition-colors">
            {isDraft ? 'Удалить черновик' : 'Архивировать'}
          </button>
        </div>

        <button onClick={onClose} className="text-mafia-muted text-xs font-mono hover:text-mafia-paper-dim transition-colors text-center">
          Закрыть
        </button>
      </motion.div>
    </div>
  );
}

export function AdminGameLevelsPage() {
  const { user } = useAuthStore();
  if (!user || !isFreeAdmin(user.email)) return <Navigate to="/" replace />;

  const { data: levels, isLoading, isError } = useAllLevels();
  const generateLevel = useGenerateLevel();

  const [difficulty, setDifficulty] = useState<Difficulty>('beginner');
  const [managing, setManaging] = useState<StoredLevel | null>(null);
  const [genError, setGenError] = useState<string | null>(null);

  async function generate() {
    setGenError(null);
    try {
      await generateLevel.mutateAsync(difficulty);
    } catch (e: unknown) {
      setGenError(e instanceof Error ? e.message : 'Ошибка генерации');
    }
  }

  const sorted = [...(levels ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const byStatus: Record<string, StoredLevel[]> = {
    generating: sorted.filter((l) => l.status === 'generating'),
    published:  sorted.filter((l) => l.status === 'published'),
    scheduled:  sorted.filter((l) => l.status === 'scheduled'),
    draft:      sorted.filter((l) => l.status === 'draft'),
    error:      sorted.filter((l) => l.status === 'error'),
    archived:   sorted.filter((l) => l.status === 'archived'),
  };

  return (
    <PageWrapper>
      <Seo title="Уровни — Управление" description="Генерация и публикация уровней детективной головоломки" path="/admin/game" />

      {managing && <ManageModal stored={managing} onClose={() => setManaging(null)} />}

      <div className="max-w-3xl mx-auto flex flex-col gap-8">
        {/* Header */}
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-mafia-muted mb-1">Администратор</p>
          <h1 className="font-display text-3xl font-bold text-mafia-gold">Уровни игры</h1>
        </div>

        <OrnamentDivider />

        {/* Generator */}
        <div className="card-vintage p-5 flex flex-col gap-4">
          <h2 className="font-mono text-xs uppercase tracking-widest text-mafia-muted">Генерировать новый уровень</h2>

          <div className="flex flex-wrap gap-2">
            {DIFFICULTIES.map((d) => (
              <label key={d.value} className={`cursor-pointer px-3 py-1.5 rounded border text-xs font-mono transition-colors ${difficulty === d.value ? 'border-mafia-gold/60 bg-mafia-gold/5 text-mafia-gold' : 'border-mafia-border text-mafia-muted hover:text-mafia-paper-dim'}`}>
                <input type="radio" name="diff" value={d.value} checked={difficulty === d.value} onChange={() => setDifficulty(d.value)} className="sr-only" />
                {d.label}
              </label>
            ))}
          </div>

          <button onClick={generate} disabled={generateLevel.isPending} className="btn-gold-solid self-start px-6 py-2 text-sm disabled:opacity-50">
            {generateLevel.isPending ? (
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 border border-mafia-gold border-t-transparent rounded-full animate-spin" />
                Отправляю…
              </span>
            ) : 'Генерировать'}
          </button>

          {genError && <p className="text-mafia-crimson text-xs">{genError}</p>}
          {generateLevel.isSuccess && (
            <p className="text-mafia-gold/80 text-xs">
              ✓ Запрос отправлен — уровень появится в списке как «Генерируется…», обычно 1–2 минуты
            </p>
          )}
        </div>

        <OrnamentDivider />

        {/* Level list */}
        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-mafia-gold/40 border-t-mafia-gold rounded-full animate-spin" />
          </div>
        )}

        {isError && <p className="text-mafia-crimson text-sm text-center">Не удалось загрузить уровни</p>}

        {!isLoading && !isError && (
          <div className="flex flex-col gap-6">
            {(['generating', 'published', 'scheduled', 'draft', 'error', 'archived'] as const).map((status) => {
              const group = byStatus[status];
              if (group.length === 0) return null;
              return (
                <div key={status} className="flex flex-col gap-2">
                  <h3 className="font-mono text-[10px] uppercase tracking-widest text-mafia-muted">
                    {STATUS_BADGE[status].label} ({group.length})
                  </h3>
                  {group.map((s) => (
                    <LevelRow key={s.levelId} stored={s} onManage={setManaging} />
                  ))}
                </div>
              );
            })}

            {sorted.length === 0 && (
              <p className="text-center text-mafia-muted text-sm py-8">Уровней пока нет — сгенерируй первый выше</p>
            )}
          </div>
        )}
      </div>
    </PageWrapper>
  );
}
